import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getEditSystemData, updateSystemAsset } from '../../../../models/assetsModel';
import Modal from '../../../../components/common/Modal';
import Button from '../../../../components/common/Button';

function EditSystemModal({ isOpen, onClose, onSuccess, system }) {
  const [formData, setFormData] = useState({
    asset_id: '',
    asset_name: '',
    asset_description: '',
    asset_parent: '0',
    asset_support: '',
    asset_sla: '',
    asset_system: 1
  });
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && system) {
      fetchEditData();
    }
  }, [isOpen, system]);

  const fetchEditData = async () => {
    try {
      setLoading(true);
      const data = await getEditSystemData(system.asset_id);
      
      if (data) {
        // Set form data from the response
        setFormData({
          asset_id: system.asset_id,
          asset_name: data.asset_name || system.asset_name || '',
          asset_description: data.asset_description || system.asset_description || '',
          asset_parent: data.asset_parent || system.asset_parent || '0',
          asset_support: data.asset_support || system.asset_support || '',
          asset_sla: data.asset_sla || system.asset_sla || '',
          asset_system: 1
        });
        
        // Set systems for parent dropdown
        if (data.systems) {
          setSystems(data.systems);
        }
      }
    } catch (error) {
      console.error('Error fetching edit data:', error);
      toast.error('Failed to load system data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.asset_name.trim()) {
      toast.error('System name is required');
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Add the required hidden fields that the PHP backend expects for systems
      const systemData = {
        ...formData,
        asset_license_key: '',
        asset_category_id: '0',
        asset_vendor_id: '0', 
        asset_cost: '0',
        asset_users: '0',
        asset_date_purchased: '',
        asset_expiry: '',
        asset_system: '1'
      };
      
      const result = await updateSystemAsset(systemData);
      
      if (result) {
        toast.success('System updated successfully!');
        onSuccess();
      }
    } catch (error) {
      console.error('Error updating system:', error);
      toast.error('Failed to update system');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      asset_id: '',
      asset_name: '',
      asset_description: '',
      asset_parent: '0',
      asset_support: '',
      asset_sla: '',
      asset_system: 1
    });
    onClose();
  };

  if (loading) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Edit System">
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Edit System">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* System Information Section */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
          <h3 className="text-sm font-semibold text-blue-900 mb-3 flex items-center">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
            </svg>
            System Information
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                System Name *
              </label>
              <input
                type="text"
                name="asset_name"
                value={formData.asset_name}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter system name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Parent System
              </label>
              <select
                name="asset_parent"
                value={formData.asset_parent}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              >
                <option value="0">None (Root System)</option>
                {systems
                  .filter(s => s.asset_id !== formData.asset_id)
                  .map((system) => (
                    <option key={system.asset_id} value={system.asset_id}>
                      {system.asset_name}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              name="asset_description"
              value={formData.asset_description}
              onChange={handleInputChange}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none"
              placeholder="Enter system description or notes..."
            />
          </div>
        </div>

        {/* Support Information Section */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-100">
          <h3 className="text-sm font-semibold text-green-900 mb-3 flex items-center">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            Support & SLA
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Support Contact
              </label>
              <textarea
                name="asset_support"
                value={formData.asset_support}
                onChange={handleInputChange}
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 resize-none"
                placeholder="Enter support contact details&#10;(one email per line)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SLA (Hours)
              </label>
              <div className="relative">
                <input
                  type="number"
                  name="asset_sla"
                  value={formData.asset_sla}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 pr-16 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  placeholder="24"
                  min="0"
                  step="1"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <span className="text-gray-500 text-sm">hours</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">Response time commitment</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2.5 text-sm font-medium"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-2.5 text-sm font-medium bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            {isSubmitting ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Updating...
              </span>
            ) : (
              'Update System'
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export default EditSystemModal;
